import { useMemo } from "react";

import CourtHearingsIcon from "@mui/icons-material/AccountBalanceRounded";
import TakeoverIcon from "@mui/icons-material/AccountBoxRounded";
import PriorityRequestsIcon from "@mui/icons-material/BookmarkRounded";
import CalculatorIcon from "@mui/icons-material/CalculateRounded";
import AppointmentsIcon from "@mui/icons-material/CalendarMonthRounded";
import AssignmentsIcon from "@mui/icons-material/ChecklistRounded";
import AttendanceListIcon from "@mui/icons-material/ContactPageRounded";
import DutyScheduleIcon from "@mui/icons-material/EventNoteRounded";
import EdictsIcon from "@mui/icons-material/GavelRounded";
import ExchangeIcon from "@mui/icons-material/HandshakeRounded";
import StatisticsIcon from "@mui/icons-material/InsertChartRounded";
import InternetRegistrationIcon from "@mui/icons-material/LanguageRounded";
import ClientIcon from "@mui/icons-material/PeopleRounded";
import SystemSettingsIcon from "@mui/icons-material/ShieldRounded";
import DashboardIcon from "@mui/icons-material/SpaceDashboardRounded";
import AmbulanzesIcon from "@mui/icons-material/SupportAgentRounded";
import { NavItem, useAlertContext } from "@shared";

import { useClientsSearchContext } from "@/common/contexts/clients-search-context/ClientsSearchContext";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import { useAmbulanzesQuery } from "@/pages/ambulanzes/hooks/useAmbulanzesQuery";
import { useAssignmentsQuery } from "@/pages/assignments/hooks/useAssignmentsQuery";
import { countExchangeBadge } from "@/pages/exchange/Exchange.utils";
import { useExchangeQuery } from "@/pages/exchange/hooks/useExchangeQuery";
import { useInternetRegistrationsQuery } from "@/pages/internet-registration/hooks/useInternetRegistrationsQuery";
import { useTakeoverQuery } from "@/pages/takeover/hooks/useTakeoverQuery";
import { AppRoutes } from "@/router/AppRoutes";
import { clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

export function useLayoutNavigation() {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const { currentUser, hasPermission } = useCurrentUserContext();
  const { lastSearchParams } = useClientsSearchContext();
  const { data: exchanges } = useExchangeQuery();
  const { data: takeovers } = useTakeoverQuery();
  const { data: ambulanzes } = useAmbulanzesQuery();
  const { data: assignments } = useAssignmentsQuery(currentUser.counselorId);
  const { data: internetRegistrations } = useInternetRegistrationsQuery();

  const clientSearchParams = useMemo(() => {
    if (lastSearchParams === null) {
      return clientPath.my(currentUser.counselorId);
    }
    return clientPath.root(lastSearchParams);
  }, [lastSearchParams, currentUser]);

  const navigationGroups: NavItem[][] = useMemo(() => {
    return [
      [{ label: "layout.navigation.dashboard", path: AppRoutes.Default, icon: <DashboardIcon /> }],
      [
        {
          label: "layout.navigation.clients",
          path: AppRoutes.Clients,
          params: clientSearchParams,
          icon: <ClientIcon />,
        },
        {
          label: "layout.navigation.ambulanzes",
          path: AppRoutes.Ambulanzes,
          icon: <AmbulanzesIcon />,
          count: ambulanzes?.openCount,
        },
        {
          label: "layout.navigation.priorityRequests",
          path: AppRoutes.PriorityRequests,
          icon: <PriorityRequestsIcon />,
        },
        {
          label: "layout.navigation.assignments",
          path: AppRoutes.Assignments,
          icon: <AssignmentsIcon />,
          count: assignments?.open.length,
        },
        {
          label: "layout.navigation.internetRegistration",
          path: AppRoutes.InternetRegistration,
          icon: <InternetRegistrationIcon />,
          count: internetRegistrations?.length,
        },
        {
          label: "layout.navigation.edikte",
          path: AppRoutes.Edicts,
          icon: <EdictsIcon />,
        },
      ],
      [
        {
          label: "layout.navigation.appointments",
          path: AppRoutes.Appointments,
          icon: <AppointmentsIcon />,
        },
        {
          label: "layout.navigation.attendanceList",
          path: AppRoutes.AttendanceList,
          icon: <AttendanceListIcon />,
        },
        {
          label: "layout.navigation.courtHearings",
          path: AppRoutes.CourtHearings,
          icon: <CourtHearingsIcon />,
        },
        {
          label: "layout.navigation.exchange",
          path: AppRoutes.Exchange,
          icon: <ExchangeIcon />,
          count: countExchangeBadge(exchanges, currentUser.counselorId),
        },
        {
          label: "layout.navigation.takeover",
          path: AppRoutes.Takeover,
          icon: <TakeoverIcon />,
          count: takeovers?.length,
        },
        // Supervisor-only for now; the read-only view for SUL staff is a separate story.
        ...(hasPermission(UserPermission.Supervisor)
          ? [
              {
                label: "layout.navigation.dutySchedule",
                path: AppRoutes.DutySchedule,
                icon: <DutyScheduleIcon />,
              } satisfies NavItem,
            ]
          : []),
      ],
      // Own group so the Rechner overview sits directly above the DropZone action, as in the design.
      [{ label: "layout.navigation.calculators", path: AppRoutes.Calculators, icon: <CalculatorIcon /> }],
    ];
  }, [
    clientSearchParams,
    exchanges,
    currentUser.counselorId,
    takeovers,
    ambulanzes,
    assignments,
    internetRegistrations,
    hasPermission,
  ]);

  const statisticsNavigationItems: NavItem[] = [
    { label: "layout.navigation.statistics", path: AppRoutes.Statistic, icon: <StatisticsIcon /> },
  ];

  const settingsNavigationItems: NavItem[] = [
    {
      label: "layout.navigation.systemSettings",
      path: AppRoutes.SystemSettings,
      icon: <SystemSettingsIcon />,
      onClick: hasPermission(UserPermission.Supervisor)
        ? undefined
        : (event) => {
            event.preventDefault();
            showError(t("layout.navigation.systemSettingsForbidden"));
          },
    },
  ];

  return { navigationGroups, statisticsNavigationItems, settingsNavigationItems };
}
